/**
 * The local player's live vitals: HP, MP and their maxima, as the server last
 * sent them (lane AH, plan 21 §8.3).
 *
 * The server regenerates mana (and HP out of combat) by a fraction of a point
 * on every tick, so the own-player schema patches at the tick rate while
 * either is below max. When those values lived only on the React `player`
 * object, every visible change built a new `player` and re-rendered `Game`,
 * the HUD and the hotbar (~9×/s while casting). They live here instead, and
 * React's `player` no longer changes when only HP or MP moves:
 * `projectPlayerSchema` ignores them when it decides whether anything changed.
 *
 * So `player.health` / `player.mana` on the React object are NOT live. They
 * hold whatever the server last sent when something else about the player
 * changed (or the values loaded with the character). Code that shows or
 * decides on vitals reads them here:
 *
 * - components: `usePlayerVital(player, v => Math.round(v.mana))`
 *   (src/game/ui/hooks/usePlayerVital.ts) — select exactly what you show,
 *   at the precision you show it, and only that re-renders;
 * - one-off reads (chat commands, action gates): `readPlayerVitals(player)`.
 *
 * Values are keyed by the character's id, so another character (or a
 * character no server has spoken for yet: pre-game screens, offline dev,
 * tests) falls back to the vitals on its own object.
 */

export interface PlayerVitals {
  health: number;
  maxHealth: number;
  mana: number;
  maxMana: number;
}

/** Anything that carries vitals and the owning character's id. */
export type VitalsSource = Partial<PlayerVitals> & { id?: string; _id?: string };

interface OwnedVitals extends PlayerVitals {
  owner: string;
}

let live: OwnedVitals | null = null;
const listeners = new Set<() => void>();

function ownerOf(character: VitalsSource | null | undefined): string | undefined {
  return character?.id || character?._id || undefined;
}

function finiteOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** The character's live vitals (the store's when it holds this character's, else its own fields). */
export function readPlayerVitals(character: VitalsSource | null | undefined): PlayerVitals {
  const owner = ownerOf(character);
  if (live && owner && live.owner === owner) return live;
  return {
    health: finiteOr(character?.health, 0),
    maxHealth: finiteOr(character?.maxHealth, 0),
    mana: finiteOr(character?.mana, 0),
    maxMana: finiteOr(character?.maxMana, 0),
  };
}

/** True when the store holds this character's vitals (a server has sent them). */
export function hasLiveVitals(character: VitalsSource | null | undefined): boolean {
  const owner = ownerOf(character);
  return Boolean(live && owner && live.owner === owner);
}

/**
 * Record vitals for `character`. Missing or non-finite fields keep their
 * current value. Subscribers hear about it only when a value changed.
 * Never call this inside a React state updater: subscribers re-render.
 */
export function writePlayerVitals(
  character: VitalsSource | null | undefined,
  patch: Partial<PlayerVitals>,
): void {
  const owner = ownerOf(character);
  if (!owner) return;
  const base = readPlayerVitals(character);
  const next: OwnedVitals = {
    owner,
    health: finiteOr(patch.health, base.health),
    maxHealth: finiteOr(patch.maxHealth, base.maxHealth),
    mana: finiteOr(patch.mana, base.mana),
    maxMana: finiteOr(patch.maxMana, base.maxMana),
  };
  if (
    live
    && live.owner === owner
    && live.health === next.health
    && live.maxHealth === next.maxHealth
    && live.mana === next.mana
    && live.maxMana === next.maxMana
  ) {
    return;
  }
  live = next;
  for (const listener of [...listeners]) listener();
}

/** Subscribe to vitals changes (useSyncExternalStore shape). */
export function subscribePlayerVitals(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** A copy of `character` carrying its live vitals (for scene restarts and saves). */
export function withLiveVitals<T extends VitalsSource>(character: T): T {
  const { health, maxHealth, mana, maxMana } = readPlayerVitals(character);
  return { ...character, health, maxHealth, mana, maxMana };
}

/** Test seam: forget the stored vitals and every subscriber. */
export function resetPlayerVitalsForTests(): void {
  live = null;
  listeners.clear();
}
